import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import {
  fetchAllAnnotatorsFailure,
  fetchAllAnnotatorsRequest,
  fetchAllAnnotatorsSuccess,
  fetchAllAnnotatorsSuccessAppend,
  fetchAnnotatorTypesFailure,
  fetchAnnotatorTypesRequest,
  fetchAnnotatorTypesSuccess,
} from 'actions/annotator';
import type {
  Annotator,
  AnnotatorTypes,
  API,
  ErrorObject,
  LanguageMatchersTypes,
  MLanguageMatcher,
  NormalizedAnnotator,
  NormalizedRelationshipType,
  NormalizedResource,
} from 'types';
import { v4 as uuidv4 } from 'uuid';

export type AnnotatorState = {
  unsavedChanges: boolean;
  items: NormalizedResource<NormalizedAnnotator>;
  filterItems: NormalizedResource<NormalizedAnnotator>;
  types: AnnotatorTypes;
  languageMatchers: NormalizedResource<MLanguageMatcher>;
  activeLanguageMatcher: string;
  loading: string[];
  relationshipTypes: NormalizedRelationshipType[];
  languageMatcherTypes: LanguageMatchersTypes;
  generatedTerms: Record<string, string[]>;
  dropPosition: number;
  error: ErrorObject | null;
  count: number;
};

type AnnotatorReducer<P = void> = (
  state: AnnotatorState,
  action: PayloadAction<P>
) => AnnotatorState;

const defaultState: AnnotatorState = {
  unsavedChanges: false,
  items: {},
  filterItems: {},
  types: {} as AnnotatorTypes,
  languageMatchers: {},
  activeLanguageMatcher: '',
  loading: [],
  relationshipTypes: [],
  languageMatcherTypes: {} as LanguageMatchersTypes,
  generatedTerms: {},
  dropPosition: -1,
  error: null,
  count: 0,
};

const handleFetchAllAnnotatorsRequest: AnnotatorReducer = (state) => ({
  ...state,
  error: null,
  loading: [...state.loading, fetchAllAnnotatorsRequest.toString()],
});

const handleFetchAllAnnotatorsSuccess: AnnotatorReducer<API.WrappedAPIResponse<Annotator>> = (
  state,
  { payload }
) => {
  const annotators: { [uuid: string]: NormalizedAnnotator } = {};
  const languageMatchers: { [uuid: string]: MLanguageMatcher } = {};
  payload.records.forEach((annotator) => {
    const { uuid, language_matchers: annotatorLanguageMatchers } = annotator;
    // @ts-ignore
    const normalizedAnnotator: NormalizedAnnotator = {
      ...annotator,
      language_matchers: annotatorLanguageMatchers.map((matcher) => {
        // @ts-ignore
        const matcherId = matcher.id || uuidv4();

        languageMatchers[matcherId] = {
          ...matcher,
          id: matcherId,
          annotator_uuid: uuid,
          saved: true,
        };

        return matcherId;
      }),
    };

    annotators[uuid] = normalizedAnnotator;
  });

  return {
    ...state,
    items: annotators,
    languageMatchers: { ...state.languageMatchers, ...languageMatchers },
    count: payload.count,
    loading: state.loading.filter((s) => s !== fetchAllAnnotatorsRequest.toString()),
  };
};

const handleFetchAllAnnotatorsFailure: AnnotatorReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchAllAnnotatorsRequest.toString()),
});

const handleFetchAnnotatorTypesRequest: AnnotatorReducer = (state) => ({
  ...state,
  error: null,
  loading: [...state.loading, fetchAnnotatorTypesRequest.toString()],
});

const handleFetchAnnotatorTypesSuccess: AnnotatorReducer<AnnotatorTypes> = (state, { payload }) => {
  const types = Object.entries(payload).reduce(
    (curr, [key, type]) => ({
      ...curr,
      [key]: {
        ...type,
        name: type.meta.name,
        description: type.meta.description,
        key,
      },
    }),
    {}
  );

  return {
    ...state,
    types: {
      ...state.types,
      ...types,
    },
    loading: state.loading.filter((s) => s !== fetchAnnotatorTypesRequest.toString()),
  };
};

const handleFetchAnnotatorTypesFailure: AnnotatorReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchAnnotatorTypesRequest.toString()),
});

const handleFetchAllAnnotatorsSuccessAppend: AnnotatorReducer<
  API.WrappedAPIResponse<NormalizedAnnotator>
> = (state, { payload }) => {
  const normalizedAnnotators: NormalizedResource<NormalizedAnnotator> = {};
  payload.records.forEach((annotator) => {
    normalizedAnnotators[annotator.uuid] = annotator;
  });
  return {
    ...state,
    items: normalizedAnnotators,
    loading: state.loading.filter((s) => s !== fetchAllAnnotatorsRequest.toString()),
    count: payload.count,
  };
};

const handlers = {
  [fetchAllAnnotatorsRequest.toString()]: handleFetchAllAnnotatorsRequest,
  [fetchAllAnnotatorsSuccess.toString()]: handleFetchAllAnnotatorsSuccess,
  [fetchAllAnnotatorsFailure.toString()]: handleFetchAllAnnotatorsFailure,

  [fetchAnnotatorTypesRequest.toString()]: handleFetchAnnotatorTypesRequest,
  [fetchAnnotatorTypesSuccess.toString()]: handleFetchAnnotatorTypesSuccess,
  [fetchAnnotatorTypesFailure.toString()]: handleFetchAnnotatorTypesFailure,

  [fetchAllAnnotatorsSuccessAppend.toString()]: handleFetchAllAnnotatorsSuccessAppend,
};

const annotatorReducer = createReducer(defaultState, handlers);

export default annotatorReducer;
